import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as Constants from '../Shared/SurveyFormConstants';
import Wysiwyg from '../../Wysiwyg';
import { isEmpty } from 'lodash';

class SurveysRadioListWidget extends Component {
  constructor(props) {
    super(props);

    const { value } = props;
    this.state = {
      // Normalize incoming value so stray OTHER_REASON tokens don't force "Other" selected
      selectedValues: this.normalizeValue(value || []),
      otherReasonValue: '',
    };
  }

  /**
   * Ensure the value array is consistent:
   * - If OTHER is not selected, drop any OTHER_REASON token.
   */
  normalizeValue(val) {
    if (!Array.isArray(val)) return [];
    const hasOther = val.includes(Constants.OTHER_KEY);
    return hasOther
      ? val
      : val.filter(
          v =>
            !(typeof v === 'string' && v.startsWith(Constants.OTHER_REASON_KEY))
        );
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      const normalized = this.normalizeValue(this.props.value || []);
      // Only update state if it actually changed to avoid extra renders
      const prev = this.state.selectedValues || [];
      const sameLength = prev.length === normalized.length;
      const same = sameLength && prev.every((x, i) => x === normalized[i]);
      if (!same) {
        this.setState({ selectedValues: normalized });
      }
    }
  }

  handleOptionChange = (event, option) => {
    // Selecting a normal option should never include OTHER_REASON tokens
    const newValue = [option.value];
    this.setState({ selectedValues: newValue }, () => {
      this.triggerOnChange(newValue);
    });
  };

  handleOtherChange = () => {
    const newValue = [Constants.OTHER_KEY];
    this.setState({ selectedValues: newValue }, () => {
      this.triggerOnChange(newValue);
    });
  };

  handleOtherReasonChange = content => {
    // Guard: only react if "Other" is currently selected
    if (!this.state.selectedValues.includes(Constants.OTHER_KEY)) return;

    const newValue = [Constants.OTHER_KEY];
    if (!isEmpty(content)) {
      newValue.push(Constants.OTHER_REASON_KEY + content);
    }
    this.setState({ selectedValues: newValue }, () => {
      this.triggerOnChange(newValue);
    });
  };

  triggerOnChange(value) {
    const { onChange } = this.props;
    if (onChange) {
      onChange(value);
    }
  }

  render() {
    const { id, disabled, options, autofocus, readonly, schema } = this.props;
    let { enumOptions, enumDisabled, inline } = options || {};
    // Filter out OTHER Key from the list to display
    enumOptions = (enumOptions || []).filter(
      o => o.value !== Constants.OTHER_KEY
    );

    const { selectedValues } = this.state;
    const otherIndex = enumOptions.length;

    const otherChecked = selectedValues.indexOf(Constants.OTHER_KEY) !== -1;
    const otherReasonToken = otherChecked
      ? selectedValues.find(el =>
          el?.toString().startsWith(Constants.OTHER_REASON_KEY)
        )
      : null;
    const otherReasonValueClean = isEmpty(otherReasonToken)
      ? ''
      : otherReasonToken.replace(Constants.OTHER_REASON_KEY, '');

    const otherDisabled =
      enumDisabled && enumDisabled.indexOf(Constants.OTHER_KEY) !== -1;
    const otherDisabledCls =
      disabled || otherDisabled || readonly ? 'disabled' : '';

    const otherTextEnabled =
      otherChecked &&
      schema?.hasOwnProperty('readOnly') &&
      otherDisabledCls !== 'disabled';

    const otherTextDisabled = schema?.hasOwnProperty('readOnly')
      ? false
      : disabled || otherDisabled || readonly;

    const otherRadio = (
      <span>
        <input
          type="radio"
          id={`${id}_${otherIndex}`}
          checked={
            (otherChecked && !schema?.hasOwnProperty('readOnly')) ||
            otherTextEnabled
          }
          disabled={disabled || otherDisabled || readonly}
          autoFocus={autofocus && otherIndex === 0}
          onChange={this.handleOtherChange}
        />
        <span>Other</span>
      </span>
    );

    let allOptions = [];
    enumOptions.forEach((option, index) => {
      allOptions.push(option.value);
      if (schema?.items?.contingentEnum) {
        schema.items.contingentEnum.forEach(contingentItem => {
          allOptions.push(`${index}_${contingentItem}`);
        });
      }
    });

    return (
      <div className="radio-list" id={id}>
        {enumOptions.map((option, index) => {
          const checked = selectedValues.indexOf(option.value) !== -1;
          const itemDisabled =
            enumDisabled && enumDisabled.indexOf(option.value) !== -1;
          const disabledCls =
            disabled || itemDisabled || readonly ? 'disabled' : '';
          const checkId = `${id}_${index}`;

          const radio = (
            <span>
              <input
                type="radio"
                id={checkId}
                checked={checked}
                disabled={disabled || itemDisabled || readonly}
                autoFocus={autofocus && index === 0}
                onChange={event => this.handleOptionChange(event, option)}
              />
              <span>{option.label}</span>
            </span>
          );

          return inline ? (
            <label key={index} className={`radio-inline ${disabledCls}`}>
              {radio}
            </label>
          ) : (
            <div key={index} className={`radio ${disabledCls}`}>
              <label>{radio}</label>
            </div>
          );
        })}

        {!schema?.showOther ? null : (
          <>
            <div key={otherIndex} className={`radio ${otherDisabledCls}`}>
              <label style={{ width: '100%' }}>{otherRadio}</label>
            </div>

            {!otherChecked && !schema?.hasOwnProperty('readOnly') ? null : (
              <div id={`${id}_${otherIndex}_other`}>
                <span style={{ fontWeight: 'bold' }}>
                  If Other selected, Please specify*
                </span>
                <Wysiwyg
                  id={`${id}_${otherIndex}_other_text`}
                  style={{ width: '100%' }}
                  required
                  disabled={otherTextDisabled || !otherChecked}
                  value={otherReasonValueClean}
                  focusOnInit={false}
                  onChange={this.handleOtherReasonChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}

SurveysRadioListWidget.defaultProps = {
  autofocus: false,
  options: {
    inline: false,
  },
};

SurveysRadioListWidget.propTypes = {
  schema: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  options: PropTypes.shape({
    enumOptions: PropTypes.array,
    inline: PropTypes.bool,
  }).isRequired,
  value: PropTypes.any,
  required: PropTypes.bool,
  readonly: PropTypes.bool,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  autofocus: PropTypes.bool,
  onChange: PropTypes.func,
};

export default SurveysRadioListWidget;
